/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import React, { useEffect, useMemo, useState } from "react";
import { SxProps, Theme } from "@mui/material/styles";
import { useAiPromptGenerationStream } from "../../hooks/useAiEventStream";
import { PromptGenerationStreamOutput } from "./PromptGenerationStreamOutput";

type PromptGenerationStreamOutputWithRandomEmptyTextProps = {
  animated?: boolean;
  emptyTexts?: string[];
  intervalMs?: number;
  sx?: SxProps<Theme>;
};

const defaultEmptyTexts = [
  "Der Legalbird zieht gerade die letzten Fusseln aus dem Tatbestand.",
  "Die KI bringt den Sachverhalt noch in eine gerichtsfeste Sitzposition.",
  "Ein Absatz sucht noch nach seiner besten Begründung.",
  "Der Legalbird federt gerade eine besonders kantige Formulierung ab.",
  "Die Argumente werden noch einmal durch den Kanzleifilter geschickt.",
  "Die KI prüft, ob der Schriftsatz auch ohne Räuspern überzeugt.",
  "Der Legalbird ordnet gerade Gedanken, Anlagen und leise Zweifel.",
  "Der Text bekommt noch einen Hauch von Substanz und Struktur.",
  "Die KI sortiert gerade Paragrafen von Bauchgefühl.",
  "Ein Satz steht noch vor dem Spiegel und übt Seriosität.",
  "Der Legalbird legt gerade die Begründung ins richtige Nest.",
  "Die Antwort wird noch mit juristischer Sorgfalt abgestaubt.",
  "Die KI sucht nach dem Ton zwischen bestimmt und charmant.",
  "Der Schriftsatz wird gerade auf Flügelschlag und Standfestigkeit geprüft.",
  "Ein Argument hat sich versteckt und wird gerade freundlich hervorgeholt.",
  "Der Legalbird zupft noch am roten Faden.",
  "Die KI bringt gerade Ordnung in den Paragrafensalat.",
  "Der Entwurf nimmt noch kurz Haltung an.",
  "Die Antwort wird gerade von unnötigem Amtsdeutsch befreit.",
  "Der Legalbird schaut, ob alle Fakten brav in Reihe sitzen.",
  "Die KI feilt noch an einer Formulierung mit Rückgrat.",
  "Ein Nebensatz braucht noch etwas juristische Erziehung.",
  "Der Text wird gerade in eine anwaltliche Flugbahn gebracht.",
  "Die Argumente bekommen noch ihre passende Reihenfolge.",
  "Der Legalbird schlägt gerade elegant mit den Flügeln der Logik.",
  "Die KI prüft, ob der Entwurf klar, knapp und kampfbereit ist.",
  "Ein Komma wurde zur Güteverhandlung geladen.",
  "Der Schriftsatz wird gerade mit Sachlichkeit geschnürt.",
  "Die Antwort bekommt noch den letzten Kanzleischliff.",
  "Der Legalbird balanciert gerade Fakten und Formulierungen auf einem Ast.",
  "Die KI macht noch einen Kontrollflug über den Sachverhalt.",
  "Ein Absatz wartet noch auf seine überzeugende Pointe.",
  "Der Text wird gerade von losen Gedanken zu festen Argumenten verdichtet.",
  "Die KI trennt gerade Wichtiges von hübschem Beiwerk.",
  "Der Legalbird räumt noch ein paar sprachliche Stolpersteine aus dem Weg.",
  "Die Antwort wird gerade in eine klare Linie gebracht.",
  "Ein besonders förmlicher Satz darf sich noch etwas entspannen.",
  "Der Legalbird prüft, ob der Entwurf genug Biss und genug Benehmen hat.",
  "Die KI setzt gerade die Fakten auf die richtige Landebahn.",
  "Der Schriftsatz bekommt noch eine Extraportion Nachvollziehbarkeit.",
  "Die Argumente werden gerade auf Alltagstauglichkeit und Gerichtsfestigkeit getestet.",
  "Der Legalbird sucht noch den elegantesten Weg durch den Aktenwald.",
  "Die KI poliert gerade den Übergang zwischen zwei Gedanken.",
  "Ein Satz wurde wegen Überlänge kurz zur Seite genommen.",
  "Der Text wird noch auf Klarheit, Kraft und Kaffeeflecken geprüft.",
  "Die Antwort faltet gerade ihre Robe ordentlich zusammen.",
  "Der Legalbird pickt noch ein paar schwammige Wörter aus dem Nest.",
  "Die KI bringt gerade den letzten Gedanken auf den Punkt.",
  "Der Entwurf wird noch einmal gegen juristisches Murmeln abgesichert.",
  "Der Legalbird landet gleich mit einem frisch formulierten Schriftsatz.",
  "Der Legalbird sortiert gerade seine Federn und die Fakten.",
  "Die KI blättert noch durch den digitalen Aktenstapel.",
  "Ein virtueller Schriftsatz macht sich gerade hübsch.",
  "Der Legalbird sucht die eleganteste Formulierung im Nest.",
  "Die Argumente werden gerade frisch aufgeplustert.",
  "Bitte kurz warten, der Paragrafenkompass richtet sich aus.",
  "Die KI prüft noch, ob der Satz auch vor Gericht eine gute Figur macht.",
  "Der Legalbird pickt gerade die besten Wörter heraus.",
  "Die digitale Robe wird noch zurechtgezupft.",
  "Die Antwort wird gerade juristisch entknittert.",
  "Ein paar Argumente stehen noch im Wartezimmer.",
  "Der Legalbird fliegt eine Ehrenrunde über die Falldaten.",
  "Die KI sucht gerade den roten Faden im Aktennest.",
  "Der Schriftsatz wird gerade aus dem Ei gepellt.",
  "Bitte nicht erschrecken, hier wird gerade juristisch gebrütet.",
  "Die KI macht noch einen letzten Blick in die digitale Kommentarspalte.",
  "Der Legalbird fragt kurz den inneren Oberamtsrat.",
  "Die Formulierungen ziehen gerade ihre Sonntagsrobe an.",
  "Die KI verhandelt noch mit einem besonders störrischen Nebensatz.",
  "Der Legalbird bringt die Argumente in Formation.",
  "Der Text wird gerade mit Paragrafen-Pfeffer gewürzt.",
  "Die KI sucht noch nach dem perfekten Einstieg ohne Anwaltslatein.",
  "Ein Komma hat Einspruch eingelegt. Wird geprüft.",
  "Der Legalbird sortiert Beweise, Gedanken und Kaffeetassen.",
  "Die Antwort ist unterwegs, musste aber kurz zur Fristenkontrolle.",
  "Die KI macht gerade einen Plausibilitäts-Salto.",
  "Der Text wird noch auf Kanzlei-Tauglichkeit geprüft.",
  "Der Legalbird hält kurz Rücksprache mit dem gesunden Menschenverstand.",
  "Die Argumentationskette wird gerade poliert.",
  "Bitte warten, die KI entwirrt den Sachverhalt mit Schnabelspitzengefühl.",
  "Der Legalbird sucht noch die Formulierung mit dem geringsten Stirnrunzeln.",
  "Die Antwort wird gerade aus juristischem Rohmaterial geschnitzt.",
  "Die KI zählt noch einmal nach, ob alle Argumente anwesend sind.",
  "Der Legalbird macht gerade Akten-Yoga.",
  "Die Formulierung sitzt fast, nur der Kragen der Robe zwickt noch.",
  "Der Text wird gerade auf unnötige Schachtelsätze untersucht.",
  "Die KI nimmt dem Schriftsatz noch die Krawatte ab.",
  "Der Legalbird pickt die Dopplungen aus dem Entwurf.",
  "Die Antwort wird gerade kanzleifein gemacht.",
  "Die KI hat den Sachverhalt im Schnabel und sucht den passenden Ast.",
  "Ein besonders ehrgeiziger Absatz möchte noch besser werden.",
  "Der Legalbird prüft, ob der Ton freundlich, klar und belastbar ist.",
  "Die KI räumt gerade den Werkzeugkasten der Argumente auf.",
  "Der Text bekommt noch einen letzten Federstrich.",
  "Die Antwort ist fast da, sie sucht nur noch den richtigen Landeplatz.",
  "Der Legalbird hat den Fall im Blick und die Tastatur im Griff.",
  "Die KI sortiert gerade zwischen wichtig, sehr wichtig und bitte nicht erwähnen.",
  "Ein juristischer Gedanke wird gerade vom Rohentwurf zum Prachtexemplar.",
  "Der Legalbird flattert noch durch die letzten Details.",
  "Die Antwort wird gerade mit etwas Kanzlei-Charme versehen.",
];

const getRandomIndex = (length: number, excludedIndex: number | null): number => {
  if (length <= 1) {
    return 0;
  }

  let nextIndex = Math.floor(Math.random() * length);

  while (nextIndex === excludedIndex) {
    nextIndex = Math.floor(Math.random() * length);
  }

  return nextIndex;
};

export function PromptGenerationStreamOutputWithRandomEmptyText({
  animated = true,
  emptyTexts = defaultEmptyTexts,
  intervalMs = 5000,
  sx,
}: PromptGenerationStreamOutputWithRandomEmptyTextProps) {
  const { text = "", status, error, streamId, isComplete = false } = useAiPromptGenerationStream();

  const [emptyTextIndex, setEmptyTextIndex] = useState(0);

  const normalizedEmptyTexts = useMemo(() => {
    return Array.isArray(emptyTexts) && emptyTexts.length > 0 ? emptyTexts : defaultEmptyTexts;
  }, [emptyTexts]);

  const hasConnectedStream = Boolean(streamId) && status !== "idle";
  const hasVisibleStreamText = text.length > 0;

  const shouldShowRandomEmptyText = hasConnectedStream && !hasVisibleStreamText && !error && !isComplete;

  useEffect(() => {
    if (!hasConnectedStream) {
      return;
    }

    setEmptyTextIndex(getRandomIndex(normalizedEmptyTexts.length, null));
  }, [hasConnectedStream, normalizedEmptyTexts.length, streamId]);

  useEffect(() => {
    if (!shouldShowRandomEmptyText || normalizedEmptyTexts.length <= 1) {
      return;
    }

    const interval = window.setInterval(() => {
      setEmptyTextIndex((currentIndex) => getRandomIndex(normalizedEmptyTexts.length, currentIndex));
    }, intervalMs);

    return () => {
      window.clearInterval(interval);
    };
  }, [intervalMs, normalizedEmptyTexts.length, shouldShowRandomEmptyText]);

  const currentEmptyText = shouldShowRandomEmptyText ? normalizedEmptyTexts[emptyTextIndex] ?? "" : "";

  return <PromptGenerationStreamOutput animated={animated} emptyText={currentEmptyText} sx={sx} />;
}
